/**
 * Programa de referidos — lado del navegador.
 *
 * Dos cosas distintas viven aquí y conviene no confundirlas:
 *
 *   · **El código del visitante.** Alguien llega por `?ref=maria4k7q`. Ese código
 *     se guarda para que, cuando compre —quizá tres días después, quizá tras
 *     registrarse y probar la aplicación—, el cobro sepa a quién comisionar. Es
 *     de quien *visita*, no de quien cobra.
 *
 *   · **La sesión del afiliado.** El token del panel de referidos, que es una
 *     autenticación aparte de la de la aplicación: un afiliado no tiene cuenta
 *     en Contabi.
 *
 * Ningún importe se calcula en este archivo. Los porcentajes que enseña la
 * página pública vienen del servidor y las comisiones las registra el servidor
 * contra el cobro real; lo que hay aquí sólo transporta un código.
 */

// ─── El código del visitante ─────────────────────────────────────────────────

const CLAVE_CODIGO = 'contabi_ref';

/**
 * 90 días. Es el plazo de atribución: quien llegó por un enlace y compra dentro
 * de ese tiempo cuenta como venta del afiliado. Sin caducidad, un código de hace
 * dos años seguiría cobrando comisión por una compra que ya no tuvo nada que ver
 * con él.
 */
const DIAS_ATRIBUCION = 90;

interface CodigoGuardado {
  codigo: string;
  guardadoEn: number;
}

const esCodigoValido = (valor: unknown): valor is string =>
  typeof valor === 'string' && /^[a-z0-9]{6,12}$/.test(valor);

/** El código con el que llegó este visitante, o `null`. */
export function codigoReferido(): string | null {
  try {
    const bruto = localStorage.getItem(CLAVE_CODIGO);
    if (!bruto) return null;

    const guardado = JSON.parse(bruto) as CodigoGuardado;
    if (!esCodigoValido(guardado?.codigo)) return null;

    const dias = (Date.now() - Number(guardado.guardadoEn ?? 0)) / 86_400_000;
    if (!Number.isFinite(dias) || dias > DIAS_ATRIBUCION) {
      localStorage.removeItem(CLAVE_CODIGO);
      return null;
    }

    return guardado.codigo;
  } catch {
    return null;
  }
}

/**
 * Lee `?ref=` de la URL actual, lo guarda y avisa al servidor del clic.
 *
 * Se llama desde el arranque de la aplicación, no desde la portada: el enlace de
 * un afiliado puede apuntar a `/planes`, a un servicio o a la página del propio
 * programa, y el módulo viejo sólo miraba la portada.
 *
 * El primer código gana. Si el visitante ya traía uno guardado y llega por otro
 * enlace, no se sobrescribe: el servidor va a hacer lo mismo con el vínculo
 * (`afiliado_referidos.user_id` es único) y así las dos mitades cuentan igual.
 */
export function capturarCodigoDeUrl(): void {
  if (typeof window === 'undefined') return;

  try {
    const params = new URLSearchParams(window.location.search);
    const bruto = String(params.get('ref') ?? '').trim().toLowerCase();
    if (!esCodigoValido(bruto)) return;

    if (!codigoReferido()) {
      const guardado: CodigoGuardado = { codigo: bruto, guardadoEn: Date.now() };
      localStorage.setItem(CLAVE_CODIGO, JSON.stringify(guardado));
    }

    // Telemetría: no bloquea nada ni importa si falla.
    void fetch('/api/referidos/clic', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ codigo: bruto }),
    }).catch(() => {});
  } catch {
    // localStorage puede estar bloqueado (modo privado, cookies de terceros).
  }
}

/** El enlace que comparte un afiliado. */
export function enlaceDeReferido(codigo: string): string {
  const base =
    (import.meta as any)?.env?.VITE_PUBLIC_SITE_URL?.replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : 'https://www.contabird.com');
  return `${base}/planes?ref=${codigo}`;
}

// ─── La sesión del afiliado ──────────────────────────────────────────────────

const CLAVE_SESION = 'contabi_afiliado';

export function tokenAfiliado(): string | null {
  try {
    return localStorage.getItem(CLAVE_SESION);
  } catch {
    return null;
  }
}

function guardarToken(token: string) {
  try {
    localStorage.setItem(CLAVE_SESION, token);
  } catch {
    // Sin almacenamiento la sesión dura lo que la pestaña; no es motivo de error.
  }
}

function borrarToken() {
  try {
    localStorage.removeItem(CLAVE_SESION);
  } catch {
    /* nada */
  }
}

// ─── Cliente HTTP ────────────────────────────────────────────────────────────

export class ErrorReferidos extends Error {
  code: string;
  status: number;

  constructor(message: string, status = 400, code = 'error') {
    super(message);
    this.name = 'ErrorReferidos';
    this.status = status;
    this.code = code;
  }
}

async function pedir<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  const token = tokenAfiliado();

  let respuesta: Response;
  try {
    respuesta = await fetch(`/api/referidos/${ruta}`, {
      ...opciones,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(opciones.headers ?? {}),
      },
    });
  } catch {
    throw new ErrorReferidos('No hay conexión con el servidor.', 0, 'sin_red');
  }

  if (respuesta.status === 204) return undefined as T;

  const datos = await respuesta.json().catch(() => ({}) as any);

  if (!respuesta.ok) {
    // Una sesión caducada se limpia aquí mismo: si no, el panel se quedaría
    // reintentando con un token que ya no sirve.
    if (respuesta.status === 401) borrarToken();
    throw new ErrorReferidos(
      datos?.error || 'Algo falló. Inténtalo de nuevo.',
      respuesta.status,
      datos?.code || 'error',
    );
  }

  return datos as T;
}

export interface FichaAfiliado {
  id: string;
  codigo: string;
  nombre: string;
  email: string;
  telefono: string;
  documento: string;
  banco: string;
  tipoCuenta: string;
  numeroCuenta: string;
  titularCuenta: string;
  creadoAt: string;
}

export interface ResumenAfiliado {
  clics: number;
  referidos: number;
  compras: number;
  pendienteCents: number;
  aprobadoCents: number;
  pagadoCents: number;
  totalCents: number;
  comprometidoCents: number;
  disponibleCents: number;
  minimoRetiroCents: number;
}

export interface ComisionAfiliado {
  id: string;
  planId: string;
  planNombre: string;
  cliente: string;
  baseCents: number;
  tasa: number;
  montoCents: number;
  moneda: string;
  estado: 'pendiente' | 'aprobada' | 'pagada' | 'anulada';
  tipo: 'primera' | 'renovacion';
  creadaAt: string;
  pagadaAt: string | null;
}

export interface RetiroAfiliado {
  id: string;
  montoCents: number;
  moneda: string;
  banco: string;
  tipoCuenta: string;
  cuenta: string;
  titular: string;
  estado: 'solicitado' | 'procesando' | 'pagado' | 'rechazado';
  nota: string | null;
  referencia: string | null;
  creadoAt: string;
  procesadoAt: string | null;
}

export interface DatosPanel {
  afiliado: FichaAfiliado;
  resumen: ResumenAfiliado;
  comisiones: ComisionAfiliado[];
  retiros: RetiroAfiliado[];
  referidos: Array<{ id: string; cliente: string; creadoAt: string }>;
  clicsPorDia: Array<{ dia: string; clics: number }>;
}

export interface PlanConComision {
  planId: string;
  nombre: string;
  tipo: 'cycle' | 'prepaid' | 'unlimited' | 'blocked';
  facturas: number | null;
  precioCents: number;
  moneda: string;
  tasa: number;
  comisionCents: number;
}

export const apiReferidos = {
  async registro(datos: {
    nombre: string;
    email: string;
    password: string;
    telefono?: string;
    documento?: string;
  }) {
    const r = await pedir<{ afiliado: FichaAfiliado; token: string }>('registro', {
      method: 'POST',
      body: JSON.stringify(datos),
    });
    guardarToken(r.token);
    return r.afiliado;
  },

  async acceso(email: string, password: string) {
    const r = await pedir<{ afiliado: FichaAfiliado; token: string }>('acceso', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    guardarToken(r.token);
    return r.afiliado;
  },

  async salir() {
    try {
      await pedir('salir', { method: 'POST' });
    } finally {
      // Aunque el servidor no conteste, la sesión local se va: quien pulsó
      // «salir» espera haber salido.
      borrarToken();
    }
  },

  panel: () => pedir<DatosPanel>('panel'),

  perfil: (cambios: Record<string, unknown>) =>
    pedir<{ afiliado: FichaAfiliado }>('perfil', {
      method: 'POST',
      body: JSON.stringify(cambios),
    }).then((r) => r.afiliado),

  retiro: (montoCents: number) =>
    pedir<{ retiro: { id: string; montoCents: number; estado: string } }>('retiro', {
      method: 'POST',
      body: JSON.stringify({ montoCents }),
    }).then((r) => r.retiro),

  tasas: () => pedir<{ planes: PlanConComision[] }>('tasas').then((r) => r.planes),

  /**
   * Avisa de que una compra hecha dentro de la aplicación ya está cobrada, para
   * que el servidor registre la comisión si venía de un referido.
   *
   * No lanza: la compra ya se hizo y la pantalla de «listo» no debe romperse
   * porque esto falle. Si falla, la comisión se puede reconstruir a mano desde
   * el PaymentIntent, que es donde está toda la información.
   */
  async registrarCompra(paymentIntentId: string) {
    try {
      await pedir('registrar-compra', {
        method: 'POST',
        body: JSON.stringify({ paymentIntentId }),
      });
    } catch (e) {
      console.warn('[referidos] no se pudo registrar la comisión', e);
    }
  },
};

// ─── Formato ─────────────────────────────────────────────────────────────────

/** Centavos de peso → «RD$ 1,234.56». */
export function pesos(cents: number | null | undefined): string {
  const valor = Number(cents ?? 0) / 100;
  return `RD$ ${valor.toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** 0.25 → «25%». */
export function porcentaje(tasa: number): string {
  return `${Math.round(tasa * 100)}%`;
}

export function fecha(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}
